import { LIFF_ID_BOT1, LIFF_ID_BOT2, MEMBER_API_URL } from '../config/env'
import { loadLiffSdk } from '../hooks/useLiff'
import { resolveBotIdFromHref } from './orderUtils'
import { resolveOrderLinkKey } from './orderLinkKey'

const tokenPromises = new Map<string, Promise<string>>()
/** ออร์เดอร์ที่เปิดแบบแขก (ไม่ได้ login LINE เจ้าของ / แอดมิน) — ใช้ปิดบังเบอร์-ที่อยู่หลังยืนยันแล้ว */
const guestOrders = new Set<string>()
/** ออร์เดอร์ที่ LINE แอดมิน (memberViewers) เปิดด้วย token staff — ค้นรายชื่อลูกค้า/ใช้ที่อยู่ลูกค้าได้ */
const staffOrders = new Set<string>()

export function isStaffOrderAccess(orderId: string): boolean {
  return staffOrders.has(String(orderId || '').trim())
}

export function isGuestOrderAccess(orderId: string): boolean {
  return guestOrders.has(String(orderId || '').trim())
}

function resolveAccessBotId(): '1' | '2' {
  if (resolveBotIdFromHref() === '2') return '2'
  try {
    if (sessionStorage.getItem('oc_liff_bot') === '2') return '2'
  } catch {
    /* ignore */
  }
  return '1'
}

/** รอ LIFF ได้ไม่เกินนี้ — เกินแล้วเปิดแบบแขก (เบราว์เซอร์ทั่วไป/เน็ตช้า) แทนการค้างหน้า */
const LIFF_ID_TOKEN_TIMEOUT_MS = 4000

/**
 * LIFF ID token ของคนที่ login LINE อยู่ ('' = ไม่ได้ login / เปิดจากเบราว์เซอร์ทั่วไป)
 * ห้ามบังคับ liff.login ที่นี่ — ใครมีลิงก์ก็ต้องเปิดดูออร์เดอร์ได้ (หน้า LINE จะ login เองใน useLiff)
 */
async function readLineIdToken(botId: '1' | '2'): Promise<string> {
  try {
    const liff = await loadLiffSdk()
    await liff.init({ liffId: botId === '2' ? LIFF_ID_BOT2 : LIFF_ID_BOT1 })
    return liff.isLoggedIn() ? String(liff.getIDToken() || '') : ''
  } catch {
    return ''
  }
}

const ADMIN_TICKET_RE = /^[A-Za-z0-9_-]{32,64}$/

/**
 * บัตรผ่านแอดมินหลังบ้าน (&av= จากปุ่ม "ดูหน้าลูกค้า" ใน Admin) — ใช้ครั้งเดียว อ่านแล้วลบออกจาก URL
 * กันรีเฟรช/คัดลอกลิงก์แล้วติดบัตรที่ใช้ไปแล้วไปด้วย · CF ตรวจจริง (memberApi orderAccess adminTicket)
 * ปุ่มเปิดลิงก์ liff.line.me → LINE ห่อ query ทั้งหมดไว้ใน liff.state จึงต้องอ่านจากในนั้นด้วย
 */
export function takeAdminViewTicket(href = window.location.href): string {
  try {
    const url = new URL(href, 'https://order.srikanett.com')
    let ticket = url.searchParams.get('av') || ''
    if (ticket) {
      url.searchParams.delete('av')
    } else {
      let state = url.searchParams.get('liff.state') || ''
      for (let attempt = 0; attempt < 2 && /%3[fd]|%26/i.test(state); attempt += 1) state = decodeURIComponent(state)
      const qIdx = state.indexOf('?')
      const prefix = qIdx >= 0 ? state.slice(0, qIdx + 1) : ''
      const sp = new URLSearchParams(qIdx >= 0 ? state.slice(qIdx + 1) : state)
      ticket = sp.get('av') || ''
      if (!ticket) return ''
      sp.delete('av')
      url.searchParams.set('liff.state', prefix + sp.toString())
    }
    try {
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
    } catch {
      /* ignore */
    }
    return ADMIN_TICKET_RE.test(ticket) ? ticket : ''
  } catch {
    return ''
  }
}

async function requestOrderToken(orderId: string): Promise<string> {
  const adminTicket = takeAdminViewTicket()
  const botId = resolveAccessBotId()
  const idToken = await Promise.race([
    readLineIdToken(botId),
    new Promise<string>((resolve) => setTimeout(() => resolve(''), LIFF_ID_TOKEN_TIMEOUT_MS)),
  ])
  const k = resolveOrderLinkKey(orderId)
  const payload: Record<string, string> = { action: 'orderAccess', orderId }
  if (idToken) payload.idToken = idToken
  if (k) payload.k = k
  if (adminTicket) payload.adminTicket = adminTicket
  const response = await fetch(MEMBER_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>
  if (!response.ok || data.ok !== true || !data.customToken) {
    throw new Error(String(data.message || 'ไม่สามารถเปิดออร์เดอร์ได้ กรุณาลองใหม่อีกครั้ง'))
  }
  if (data.guest === true) guestOrders.add(orderId)
  else guestOrders.delete(orderId)
  if (data.staff === true) staffOrders.add(orderId)
  else staffOrders.delete(orderId)
  return String(data.customToken)
}

export function authorizeOrderAccess(orderId: string): Promise<string> {
  const normalized = String(orderId || '').trim()
  if (!normalized) return Promise.reject(new Error('ไม่พบเลขออร์เดอร์'))
  const existing = tokenPromises.get(normalized)
  if (existing) return existing
  const promise = requestOrderToken(normalized).catch((error) => {
    tokenPromises.delete(normalized)
    throw error
  })
  tokenPromises.set(normalized, promise)
  return promise
}
